import { TFunction } from 'i18next';
import { theme } from 'theme';
import { MyAppointmentsEvent } from 'types';
import { formatSubscriptionDate, getTimeInterval } from 'utils/dates';

import { escapeHtml } from './Message/RichTextEditor/serialize';

export const isHttpUrl = (value?: string | null): value is string =>
  !!value && /^https?:\/\//i.test(value.trim());

export const formatEventWhen = (
  event: MyAppointmentsEvent,
  timeFormat: 12 | 24,
  t: TFunction,
  language: string,
): string => {
  const from = new Date(event.from);
  const formatted = formatSubscriptionDate(from, language);
  const date = formatted === '-' ? '' : formatted;
  if (event.allDay || !event.to) return date;
  const time = getTimeInterval(from, new Date(event.to), timeFormat, t);
  return time ? `${date} · ${time}` : date;
};

const accentColor = (color?: string | null): string =>
  color && /^#[0-9a-f]{3,8}$/i.test(color)
    ? color
    : theme.color.brightTurquoise;

const DATE_EMOJI = '&#128197;';
const VENUE_EMOJI = '&#128205;';

const metaCell = (text: string, font: string, ink: string, pad: string) =>
  `<tr><td style="padding-top:${pad};font-family:${font};font-size:13px;line-height:18px;color:${ink};">${text}</td></tr>`;

const outlookCard = (markup: string, background: string, border: string) =>
  '<!--[if mso]>' +
  '<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:office" ' +
  'style="width:100%;mso-width-percent:1000;mso-fit-shape-to-text:true;" arcsize="18%" stroke="t" ' +
  `strokecolor="${border}" strokeweight="1px" fillcolor="${background}">` +
  '<v:textbox style="mso-fit-shape-to-text:true" inset="16px,14px,16px,14px">' +
  '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">' +
  `<tr><td>${markup}</td></tr></table>` +
  '</v:textbox></v:roundrect><![endif]-->';

const roundedCard = (
  markup: string,
  background: string,
  border: string,
  bar: string,
  font: string,
) =>
  '<!--[if !mso]><!-->' +
  '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ' +
  'style="border-collapse:separate !important;border-spacing:0;width:100%;">' +
  `<tr><td bgcolor="${background}" style="background-color:${background};border:1px solid ${border};` +
  `border-left:4px solid ${bar};border-radius:12px;-webkit-border-radius:12px;padding:14px 16px;">` +
  `<div style="border-radius:12px;-webkit-border-radius:12px;background-color:${background};font-family:${font};">${markup}</div>` +
  '</td></tr></table><!--<![endif]-->';

const outlookButton = (
  url: string,
  label: string,
  width: number,
  background: string,
  text: string,
  font: string,
) =>
  '<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:office" ' +
  `href="${url}" style="height:32px;v-text-anchor:middle;width:${width}px;" arcsize="50%" stroke="f" ` +
  `fillcolor="${background}"><w:anchorlock/>` +
  `<center style="color:${text};font-family:${font};font-size:13px;font-weight:bold;">${label}</center>` +
  '</v:roundrect>';

const roundedButton = (
  url: string,
  label: string,
  background: string,
  text: string,
  font: string,
) =>
  '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="right" ' +
  'style="border-collapse:separate !important;border-spacing:0;">' +
  `<tr><td align="center" valign="middle" height="32" bgcolor="${background}" ` +
  `style="height:32px;background-color:${background};border-radius:8px;-webkit-border-radius:8px;padding:0 12px;">` +
  `<a href="${url}" target="_blank" style="display:inline-block;font-family:${font};font-size:13px;` +
  `font-weight:600;line-height:32px;color:${text};text-decoration:none;white-space:nowrap;">` +
  `<font color="${text}" style="color:${text};text-decoration:none;">${label}</font>` +
  '</a></td></tr></table>';

export const buildEventEmailBlock = ({
  title,
  when,
  where,
  url,
  viewLabel,
  accent,
}: {
  title: string;
  when: string;
  where?: string;
  url?: string | null;
  viewLabel: string;
  accent?: string | null;
}): string => {
  const color = theme.color;
  const font = 'Arial,Helvetica,sans-serif';
  const safeUrl = isHttpUrl(url) ? escapeHtml(url.trim()) : '';
  const safeLabel = escapeHtml(viewLabel);
  const bar = accentColor(accent);
  const ink = color.BlueBayoux;
  const whenRow = when
    ? metaCell(`${DATE_EMOJI}&nbsp;${escapeHtml(when)}`, font, ink, '4px')
    : '';
  const whereRow = where
    ? metaCell(`${VENUE_EMOJI}&nbsp;${escapeHtml(where)}`, font, ink, '2px')
    : '';
  const buttonWidth = Math.min(
    220,
    Math.max(56, Math.round([...viewLabel].length * 8 + 28)),
  );
  const layout = (button: string) =>
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ' +
    'style="border-collapse:separate;border-spacing:0;"><tr>' +
    `<td valign="middle" width="100%" style="padding-right:16px;font-family:${font};">` +
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" ' +
    'style="border-collapse:separate;border-spacing:0;">' +
    `<tr><td style="font-family:${font};font-size:16px;font-weight:bold;line-height:22px;color:${
      color.pickledBluewood
    };">${escapeHtml(title)}</td></tr>` +
    whenRow +
    whereRow +
    '</table></td>' +
    `<td valign="middle" align="right" style="white-space:nowrap;font-family:${font};">${button}</td>` +
    '</tr></table>';
  const outlookAction = safeUrl
    ? outlookButton(
        safeUrl,
        safeLabel,
        buttonWidth,
        color.brightTurquoise,
        color.black,
        font,
      )
    : '';
  const otherAction = safeUrl
    ? roundedButton(
        safeUrl,
        safeLabel,
        color.brightTurquoise,
        color.black,
        font,
      )
    : '';

  return (
    outlookCard(layout(outlookAction), color.aquaSqueeze, color.porcelain) +
    roundedCard(
      layout(otherAction),
      color.aquaSqueeze,
      color.porcelain,
      bar,
      font,
    )
  );
};

export const composeEmailBody = (
  eventBlock: string,
  message: string,
): string => {
  const note = message || '';
  if (!eventBlock) return note;
  if (!note.trim()) return eventBlock;
  return `${eventBlock}\n${note}`;
};
